import React, {useCallback, useMemo, useState} from 'react'
import styled from 'styled-components'
import {LIST_END_REACHED_THRESHOLD} from 'consts'
import {CSSHtmlProperties} from 'types'
import {View} from '../View'
import {ListProps} from './ListProps'

interface StyledItemContainerProps extends CSSHtmlProperties<HTMLDivElement> {
  width: string
  height?: number
}

interface StyledScrollbarProps {
  scrollbar?: boolean
}
const StyledBaseContainer = styled.div`
  position: relative;
  flex: 1;
`

const StyledListContainer = styled.div<StyledScrollbarProps>`
  display: flex;
  flex-direction: column;
  position: absolute;
  width: 100%;
  height: 100%;
  overflow-y: scroll;
  ::-webkit-scrollbar {
    width: ${({scrollbar}) => (scrollbar ? undefined : 0)};
  }
  ${({scrollbar}) => ({
    scrollbarWidth: scrollbar ? undefined : 'none',
  })}
`

const StyledContainer = styled(View)`
  display: flex;
  flex-direction: column;
  flex: 1;
  box-sizing: border-box;
`

const StyledContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`

const StyledSectionItem = styled.div`
  display: flex;
  flex-direction: row;
`

const StyledItemContainer = styled.div<StyledItemContainerProps>`
  ${({width, height}) => ({
    width,
    height,
  })}
  display: flex;
  flex-direction: column;
`

const StyledEmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

export default function List<T>({
  data: baseItems,
  numColumns = 1,
  contentContainerStyle,
  listEmptyElement,
  listHeaderElement,
  listFooterElement,
  onEndReachedThreshold = LIST_END_REACHED_THRESHOLD,
  onEndReached,
  keyExtractor,
  renderItem,
  scrollbar = true,
  ...props
}: ListProps<T>) {
  const [endZone, setEndZone] = useState(false)
  const items = useState<ReadonlyArray<T>>([])[0]
  const width = useMemo(() => `${100 / numColumns}%`, [numColumns])
  const data = useMemo(() => (baseItems ? [...baseItems, ...items] : items), [
    baseItems,
    items,
  ])
  const numRows = useMemo(() => Math.ceil(data.length / numColumns), [
    data,
    numColumns,
  ])

  const handleRenderColumns = useCallback(
    (indexRow: number) => {
      const indexStart = indexRow * numColumns
      const indexEnd = (indexRow + 1) * numColumns
      const indexLimit = Math.min(indexEnd, data.length)
      const length = indexLimit - indexStart

      return Array.from({length}, (_, indexColumn) => {
        const index = indexStart + indexColumn
        const item = data[index]

        return (
          <StyledItemContainer key={keyExtractor(item)} width={width}>
            {renderItem(item, index)}
          </StyledItemContainer>
        )
      })
    },
    [data, width, numColumns, keyExtractor, renderItem],
  )

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement, UIEvent>) => {
      if (onEndReached) {
        const {offsetHeight, scrollHeight, scrollTop} = event.currentTarget
        const dif = scrollHeight - (scrollTop + offsetHeight)

        if (!endZone && dif <= onEndReachedThreshold) {
          onEndReached({distanceFromEnd: dif})
          setEndZone(true)
        } else if (endZone && dif > onEndReachedThreshold) {
          setEndZone(false)
        }
      }
    },
    [onEndReachedThreshold, endZone, onEndReached],
  )

  const handleRenderContent = useMemo(
    () =>
      data.length ? (
        <StyledContentContainer style={contentContainerStyle}>
          {Array.from({length: numRows}, (_, index) => (
            <StyledSectionItem key={index}>
              {handleRenderColumns(index)}
            </StyledSectionItem>
          ))}
        </StyledContentContainer>
      ) : null,
    [data, numRows, contentContainerStyle, handleRenderColumns],
  )

  const handleRenderEmpty = useMemo(
    () =>
      data.length === 0 &&
      listEmptyElement && (
        <StyledEmptyContainer>{listEmptyElement}</StyledEmptyContainer>
      ),
    [data, listEmptyElement],
  )

  return (
    <StyledBaseContainer>
      <StyledListContainer onScroll={handleScroll} scrollbar={scrollbar}>
        <StyledContainer {...props}>
          {listHeaderElement}
          {handleRenderContent}
          {handleRenderEmpty}
          {listFooterElement}
        </StyledContainer>
      </StyledListContainer>
    </StyledBaseContainer>
  )
}
